'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { resolveLocale } from '@/lib/date-utils';
type PropsType = Omit<React.ComponentPropsWithoutRef<'button'>, 'value' | 'onChange'> & {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  locale?: string;
};
export function MonthPicker(t0) {
  const $ = _c(34);
  let disabled;
  let isInvalid;
  let onChange;
  let placeholder;
  let props;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      placeholder,
      disabled,
      isInvalid,
      locale: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = disabled;
    $[2] = isInvalid;
    $[3] = onChange;
    $[4] = placeholder;
    $[5] = props;
    $[6] = t1;
    $[7] = value;
  } else {
    disabled = $[1];
    isInvalid = $[2];
    onChange = $[3];
    placeholder = $[4];
    props = $[5];
    t1 = $[6];
    value = $[7];
  }
  const locale = t1 === undefined ? "en-US" : t1;
  const dateFnsLocale = resolveLocale(locale);
  const [open, setOpen] = React.useState(false);
  const parsed = value ? (() => {
    const [y, m] = value.split("-").map(Number);
    return Number.isNaN(y) || Number.isNaN(m) ? null : {
      year: y,
      month: m - 1
    };
  })() : null;
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Date().getFullYear();
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const currentYear = t2;
  const [viewYear, setViewYear] = React.useState(parsed?.year ?? currentYear);
  React.useEffect(() => {
    setViewYear(parsed?.year ?? currentYear);
  }, [currentYear, parsed?.year]);
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = Array.from({
      length: currentYear - 1900 + 1
    }, (_, i) => currentYear - i);
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const years = t3;
  const months = Array.from({
    length: 12
  }, _temp);
  let t4;
  if ($[10] !== onChange || $[11] !== setOpen || $[12] !== viewYear) {
    t4 = monthIndex => {
      const mm = String(monthIndex + 1).padStart(2, "0");
      onChange?.(`${viewYear}-${mm}`);
      setOpen(false);
    };
    $[10] = onChange;
    $[11] = setOpen;
    $[12] = viewYear;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleSelect = t4;
  const displayValue = parsed ? format(new Date(parsed.year, parsed.month, 1), "MMMM yyyy", {
    locale: dateFnsLocale
  }) : null;
  const t5 = !displayValue && "text-muted-foreground";
  const t6 = isInvalid && "border-destructive focus-visible:ring-destructive";
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = cn("h-9 w-full justify-start gap-1 text-left font-normal", t5, t6);
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <CalendarIcon className="mr-1 h-4 w-4 shrink-0" />;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const T0 = PopoverContent;
  const t10 = "w-72 p-3";
  const t11 = "start";
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Button type="button" variant="outline" size="icon" className="h-7 w-7 shrink-0" onClick={() => setViewYear(_temp2)}><ChevronLeftIcon className="h-4 w-4" /></Button>;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = String(viewYear);
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = val => setViewYear(Number(val));
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <SelectTrigger className="h-7 flex-1 text-sm"><SelectValue /></SelectTrigger>;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SelectContent className="max-h-48">{years.map(_temp3)}</SelectContent>;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t13) {
    t17 = <Select value={t13} onValueChange={t14}>{t15}{t16}</Select>;
    $[23] = t13;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Button type="button" variant="outline" size="icon" className="h-7 w-7 shrink-0" onClick={() => setViewYear(_temp4)}><ChevronRightIcon className="h-4 w-4" /></Button>;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t17) {
    t19 = <div className="mb-3 flex items-center gap-2">{t12}{t17}{t18}</div>;
    $[26] = t17;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  const t20 = "grid grid-cols-3 gap-1";
  const t21 = months.map(month => {
    const isSelected = parsed?.year === viewYear && parsed?.month === month;
    const label = format(new Date(viewYear, month, 1), "MMM", {
      locale: dateFnsLocale
    });
    return <Button key={month} type="button" variant={isSelected ? "default" : "ghost"} className="h-9 text-sm" onClick={() => handleSelect(month)}>{label}</Button>;
  });
  let t22;
  if ($[28] !== t21) {
    t22 = <div className={t20}>{t21}</div>;
    $[28] = t21;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] !== T0 || $[31] !== t19 || $[32] !== t22) {
    t23 = <T0 className={t10} align={t11}>{t19}{t22}</T0>;
    $[30] = T0;
    $[31] = t19;
    $[32] = t22;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  return <Popover open={open} onOpenChange={setOpen}><PopoverTrigger asChild={true}><Button type="button" variant="outline" disabled={disabled} className={t7} {...props}>{t8}<span className="flex-1">{displayValue ?? <span className="text-muted-foreground">{placeholder ?? "Pick a month"}</span>}</span>{t9}</Button></PopoverTrigger>{t23}</Popover>;
}
function _temp4(y_1) {
  return y_1 + 1;
}
function _temp3(year) {
  return <SelectItem key={year} value={String(year)} className="text-sm">{year}</SelectItem>;
}
function _temp2(y_0) {
  return y_0 - 1;
}
function _temp(__0, i_0) {
  return i_0;
}